import { captureException, startSpan } from "@sentry/react";

import { IPresetDTO } from "../../types";
import { doDelete, doGet, doPost, doPut } from "../core/http";

export const savePreset = async (
    preset: IPresetDTO
): Promise<IPresetDTO | { error: string }> => {
    const url = "/api/v1/presets";
    const formData = new FormData();
    formData.append("prompt", preset.prompt);
    formData.append("artStyle", preset.artStyle);
    formData.append("theme", preset.theme);
    formData.append("name", preset.name);
    formData.append("premium", preset.premium.toString());

    if (preset.freeGenModelDTO) {
        formData.append(
            "freeGenModelDTO",
            JSON.stringify(preset.freeGenModelDTO)
        );
    }

    Object.entries(preset.requestFields).forEach(([key, value]) => {
        formData.append(`requestFields[${key}]`, value);
    });
    if (preset.file) {
        formData.append("file", preset.file);
    }
    formData.append("presetType", preset.presetType);
    if (preset.additionalField) {
        formData.append("additionalField", preset.additionalField);
    }

    try {
        const { data, error } = await doPost(url, true, formData);
        if (error) {
            captureException(error);
            return { error: "Failed to save preset." };
        }
        return data as IPresetDTO;
    } catch (e) {
        captureException(e);
        return { error: "Failed to save preset." };
    }
};

export const getPreset = async (
    id: string
): Promise<IPresetDTO | { error: string }> => {
    const url = `/api/v1/presets/${id}`;

    try {
        const { data, error } = await doGet(url, true);
        if (error) {
            captureException(error);
            return { error: "Failed to get preset." };
        }
        return data as IPresetDTO;
    } catch (e) {
        captureException(e);
        return { error: "Failed to get preset." };
    }
};

export const updatePreset = async (
    id: string,
    preset: IPresetDTO
): Promise<IPresetDTO | { error: string }> => {
    const url = `/api/v1/presets/${id}`;
    const formData = new FormData();
    formData.append("prompt", preset.prompt);
    formData.append("artStyle", preset.artStyle);
    formData.append("theme", preset.theme);
    formData.append("name", preset.name);
    formData.append("premium", preset.premium.toString());

    if (preset.freeGenModelDTO) {
        formData.append(
            "freeGenModelDTO",
            JSON.stringify(preset.freeGenModelDTO)
        );
    }

    Object.entries(preset.requestFields).forEach(([key, value]) => {
        formData.append(`requestFields[${key}]`, value);
    });
    if (preset.file) {
        formData.append("file", preset.file);
    }
    formData.append("presetType", preset.presetType);
    if (preset.additionalField) {
        formData.append("additionalField", preset.additionalField);
    }

    try {
        const { data, error } = await doPut(url, true, formData);
        if (error) {
            captureException(error);
            return { error: "Failed to update preset." };
        }
        return data as IPresetDTO;
    } catch (e) {
        captureException(e);
        return { error: "Failed to update preset." };
    }
};

export const listPresets = async (
    presetType?: string
): Promise<IPresetDTO[] | { error: string }> => {
    const url = presetType
        ? `/api/v1/presets?presetType=${presetType}`
        : "/api/v1/presets";

    try {
        const { data, error } = await doGet(url, true);
        if (error) {
            captureException(error);
            return { error: "Failed to list presets." };
        }
        return data as IPresetDTO[];
    } catch (e) {
        captureException(e);
        return { error: "Failed to list presets." };
    }
};

export const deletePreset = async (
    presetId: string
): Promise<{ error?: string }> => {
    const url = `/api/v1/presets/${presetId}`;
    try {
        const { error } = await startSpan({ name: "deletePreset" }, () => {
            return doDelete(url, true);
        });

        if (error) {
            captureException(error);
            return { error: "Failed to delete preset." };
        }

        return {};
    } catch (e) {
        captureException(e);
        if (e === "User is not logged in.") {
            return { error: e };
        }
        return { error: "Failed to delete preset." };
    }
};
